import React from 'react'
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  makeStyles,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { SURVEY_LIMITS } from '@paintscout/util/engagement'
import type { DraftAnswer, SurveyQuestionView } from './surveyForm'
import { ratingValues } from './surveyForm'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'block',
    marginBottom: theme.spacing(3)
  },
  prompt: {
    display: 'block',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    fontWeight: 500
  },
  ratingRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  ratingButton: {
    minWidth: 40
  },
  ratingLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5)
  },
  error: {
    marginTop: theme.spacing(0.5)
  }
}))

export interface SurveyQuestionFieldProps {
  question: SurveyQuestionView
  value: DraftAnswer | undefined
  error?: string
  disabled?: boolean
  onChoice: (value: string, checked: boolean) => void
  onRating: (rating: number) => void
  onText: (text: string) => void
}

/** One survey question, by type: single choice, multiple choice, rating or free text (#25349). */
export default function SurveyQuestionField({
  question,
  value,
  error,
  disabled,
  onChoice,
  onRating,
  onText
}: SurveyQuestionFieldProps) {
  const classes = useStyles()
  const labelId = `survey-question-${question.id}`
  const options = question.options ?? []
  const choices = value?.choices ?? []

  let input: React.ReactNode = null
  switch (question.type) {
    case 'singleChoice':
      input = (
        <RadioGroup
          aria-labelledby={labelId}
          value={choices[0] ?? ''}
          onChange={(event) => onChoice(event.target.value, true)}
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={option.label}
              disabled={disabled}
            />
          ))}
        </RadioGroup>
      )
      break
    case 'multipleChoice':
      input = (
        <div role="group" aria-labelledby={labelId}>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={choices.includes(option.value)}
                  onChange={(event) => onChoice(option.value, event.target.checked)}
                />
              }
              label={option.label}
              disabled={disabled}
            />
          ))}
        </div>
      )
      break
    case 'rating':
      input = (
        <div>
          <div className={classes.ratingRow} role="radiogroup" aria-labelledby={labelId}>
            {ratingValues(question).map((rating) => (
              <Button
                key={rating}
                className={classes.ratingButton}
                variant={value?.rating === rating ? 'contained' : 'outlined'}
                color="primary"
                role="radio"
                aria-checked={value?.rating === rating}
                disabled={disabled}
                onClick={() => onRating(rating)}
              >
                {rating}
              </Button>
            ))}
          </div>
          {(question.minLabel || question.maxLabel) && (
            <div className={classes.ratingLabels}>
              <Typography variant="caption">{question.minLabel}</Typography>
              <Typography variant="caption">{question.maxLabel}</Typography>
            </div>
          )}
        </div>
      )
      break
    case 'text':
      input = (
        <TextField
          inputProps={{ 'aria-labelledby': labelId, maxLength: SURVEY_LIMITS.text }}
          value={value?.text ?? ''}
          onChange={(event) => onText(event.target.value)}
          disabled={disabled}
          multiline
          minRows={3}
          fullWidth
          variant="outlined"
        />
      )
      break
    default:
      return null
  }

  return (
    <FormControl className={classes.root} error={!!error} fullWidth>
      <FormLabel id={labelId} className={classes.prompt}>
        {question.prompt}
        {question.required ? ' *' : ''}
      </FormLabel>
      {input}
      {error && (
        <Typography className={classes.error} variant="caption" color="error" role="alert">
          {error}
        </Typography>
      )}
    </FormControl>
  )
}
